<template>
    <div>
    <div class="flex flex-col items-start w-full pb-4">
        <div class="flex items-start gap-1 w-full pb-2">
            <span class="text-slate-600 dark:text-slate-300 text-sm font-medium leading-5">Files</span>
            <span class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">(optional)</span>
        </div>

        <div
            class="upload-container w-full h-40 md:h-32 relative border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg text-center bg-white dark:bg-slate-800 flex items-center justify-center overflow-hidden"
            :class="{ 'drag-over border-slate-800 bg-slate-50 dark:bg-slate-900/40': isDragOver }"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @dragend.prevent="onDragLeave"
            @drop.prevent="onDrop"
        >
            <div v-if="!isDragOver" class="flex flex-col justify-center items-center gap-3 w-full p-4 h-28">
                <div class="flex flex-col items-center gap-1">
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">
                        Upload or drag & drop receipts, screenshots, and more
                    </span>
                    <span class="text-slate-500 dark:text-slate-400 text-sm font-normal leading-5">
                        We support PNG, JPEG, DOC, PDF under 10 MB
                    </span>
                </div>
                <button
                    @click="triggerFileUpload"
                    class="flex items-center gap-2 px-3 py-2 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-slate-800 focus:border-slate-800 transition-colors duration-200"
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="text-slate-800 dark:text-slate-100" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M3.33333 9.79167C3.79357 9.79167 4.16667 10.1648 4.16667 10.625V15C4.16667 15.4602 4.53976 15.8333 5 15.8333H15C15.4602 15.8333 15.8333 15.4602 15.8333 15V10.625C15.8333 10.1648 16.2064 9.79167 16.6667 9.79167C17.1269 9.79167 17.5 10.1648 17.5 10.625V15C17.5 16.3807 16.3807 17.5 15 17.5H5C3.61929 17.5 2.5 16.3807 2.5 15V10.625C2.5 10.1648 2.8731 9.79167 3.33333 9.79167Z" fill="currentColor" />
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.99999 2.5C10.221 2.5 10.433 2.5878 10.5892 2.74408L14.3393 6.49408C14.6647 6.81951 14.6647 7.34715 14.3393 7.67259C14.0138 7.99803 13.4862 7.99803 13.1607 7.67259L10.8333 5.34517V12.7083C10.8333 13.1686 10.4602 13.5417 9.99999 13.5417C9.53975 13.5417 9.16666 13.1686 9.16666 12.7083V5.34518L6.83926 7.67259C6.51382 7.99803 5.98618 7.99803 5.66075 7.67259C5.33531 7.34715 5.33531 6.81952 5.66074 6.49408L9.41073 2.74408C9.56701 2.5878 9.77898 2.5 9.99999 2.5Z" fill="currentColor" />
                    </svg>
                    <span class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5">Upload</span>
                </button>
            </div>

            <div v-else class="drag-info flex flex-col items-center justify-center gap-2 text-slate-400">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M4 12.75c.41421 0 .75.3358.75.75v4c0 .4142.33579.75.75.75h13c.4142 0 .75-.3358.75-.75v-4c0-.4142.3358-.75.75-.75s.75.3358.75.75v4c0 1.2426-1.0074 2.25-2.25 2.25H5.5c-1.24264 0-2.25-1.0074-2.25-2.25v-4c0-.4142.33579-.75.75-.75z" fill="currentColor" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M12 4c.2652 0 .5196.10536.7071.29289l4.5 4.5c.2929.29289.2929.76777 0 1.06061s-.7677.2929-1.0606 0L12.75 6.46967V16c0 .4142-.3358.75-.75.75s-.75-.3358-.75-.75V6.46967L7.85355 9.8635c-.29289.2929-.76777.2929-1.06066 0s-.29289-.76772 0-1.06061l4.5-4.5C11.4804 4.10536 11.7348 4 12 4z" fill="currentColor" />
                </svg>
                <strong class="text-sm font-medium">Drop files here</strong>
            </div>

            <input
                type="file"
                @change="handleFileUpload"
                accept="image/jpeg,image/png,application/pdf,application/msword"
                class="hidden"
                ref="fileInput"
                multiple
            />
        </div>

        <!-- File Preview Section -->
        <div v-if="uploadedFiles.length > 0" class="flex flex-wrap pt-4 items-start gap-4 w-full">
            <div
                v-for="(file, index) in uploadedFiles"
                :key="index"
                class="relative flex flex-col items-start w-[121.6px] gap-2"
            >
                <div
                    class="relative w-full h-24 flex justify-center items-center gap-2.5 rounded-lg border border-black/[0.08] overflow-hidden bg-slate-200 dark:bg-slate-700 cursor-pointer"
                    :style="(file.type || file.mime_type || '').includes('image') && file.previewUrl
                        ? `background-image: url(${file.previewUrl}); background-position: center; background-size: cover; background-repeat: no-repeat;`
                        : ''"
                    @click="openFileViewer(file)"
                >
                    <template v-if="!file.type?.includes('image')">
                        <div v-if="isPdfFile(file)" class="flex flex-col items-center justify-center w-full h-full bg-slate-100 dark:bg-slate-700">
                            <svg class="w-10 h-10 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span class="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">PDF</span>
                        </div>
                        <div v-else-if="isWordFile(file)" class="flex flex-col items-center justify-center w-full h-full bg-slate-100 dark:bg-slate-700">
                            <svg class="w-10 h-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span class="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">DOC</span>
                        </div>
                        <div v-else class="flex flex-col items-center justify-center w-full h-full bg-slate-100 dark:bg-slate-700">
                            <svg class="w-10 h-10 text-slate-500 dark:text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span class="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">{{ getFileExtension(file) }}</span>
                        </div>
                    </template>
                </div>

                <div class="w-full text-slate-500 dark:text-slate-400 text-sm leading-5 line-clamp-2 overflow-hidden text-ellipsis">
                    {{ file.name }}
                </div>

                <button
                    @click="removeFile(index)"
                    class="absolute -right-2 -top-2 flex justify-center items-center w-8 h-8 rounded bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm"
                >
                    <Icon icon="trash" :width="16" :height="16" class="text-slate-500 dark:text-slate-400" />
                </button>
            </div>
        </div>
    </div>

    <teleport to="body">
        <FileViewer :show="showFileViewer" :file="selectedFile" @close="closeFileViewer" />
    </teleport>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import { notify } from 'notiwind';
import FileViewer from '@/components/trips/redemptions/FileViewer.vue';
import Icon from '@/components/ui/icon/icon.vue';

const tripsStore = useTripsStore();
const { activeRedemption, activeTrip } = storeToRefs(tripsStore);

const fileInput = ref(null);
const uploadedFiles = ref([]);
const isDragOver = ref(false);
const showFileViewer = ref(false);
const selectedFile = ref(null);

// Track every blob: URL we create so we can revoke survivors on unmount —
// e.g. when the modal closes while an upload is still in-flight.
const createdObjectUrls = new Set();
const createObjectUrl = (file) => {
    const url = URL.createObjectURL(file);
    createdObjectUrls.add(url);
    return url;
};
const revokeObjectUrl = (url) => {
    if (!url) return;
    try { URL.revokeObjectURL(url); } catch (e) {}
    createdObjectUrls.delete(url);
};

onMounted(() => { loadExistingFiles(); });

onBeforeUnmount(() => {
    createdObjectUrls.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch (e) {}
    });
    createdObjectUrls.clear();
});

watch(() => activeRedemption.value?.id, () => {
    if (activeRedemption.value) {
        loadExistingFiles();
    }
});

function loadExistingFiles() {
    const files = activeRedemption.value?.attachments || activeRedemption.value?.files || [];
    uploadedFiles.value = files.map((file) => {
        const fileType = file.type || file.mime_type || '';
        const isImage = fileType.includes('image');
        let fileUrl = file.url || '';
        if (isImage && fileUrl && file.file_name && !fileUrl.endsWith(file.file_name)) {
            if (!fileUrl.endsWith('/')) fileUrl += '/';
            fileUrl += file.file_name;
        }
        return {
            id: file.id, name: file.name || file.file_name || 'file',
            type: fileType, previewUrl: isImage ? fileUrl : null,
            cdnUrl: fileUrl, url: file.url, isUploading: false,
        };
    });
}

const triggerFileUpload = () => { fileInput.value.click(); };

function handleFileUpload(event) {
    validateAndProcessFiles(event.target.files);
}

function openFileViewer(file) {
    const fileType = file?.type || file?.mime_type || '';
    const fileName = file?.name || file?.file_name || '';
    const isImage = fileType.includes('image') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
    if (isImage || isPdfFile(file)) {
        selectedFile.value = file;
        showFileViewer.value = true;
    } else if (file?.url || file?.cdnUrl) {
        window.open(file.url || file.cdnUrl, '_blank');
    }
}

function isPdfFile(file) {
    if (!file) return false;
    const type = file.type || file.mime_type || '';
    const name = file.name || file.file_name || '';
    return type.includes('pdf') || name.toLowerCase().endsWith('.pdf');
}

function isWordFile(file) {
    if (!file) return false;
    const type = file.type || file.mime_type || '';
    const name = file.name || file.file_name || '';
    const lowerName = name.toLowerCase();
    return type.includes('word') || type.includes('doc') || lowerName.endsWith('.doc') || lowerName.endsWith('.docx') || lowerName.endsWith('.rtf');
}

function getFileExtension(file) {
    if (!file) return '';
    const name = file.name || file.file_name || '';
    const parts = name.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
}

function closeFileViewer() {
    showFileViewer.value = false;
    selectedFile.value = null;
}

const uploadToCDN = async (file) => {
    try {
        const response = await window.ApiService.uploadFile(
            `/trips/${activeTrip.value.uuid}/redemptions/upload`, file, {}, {}
        );
        const { data } = response;
        if (!data.files || !data.files.length) throw new Error('Server response missing files');
        const uploadedFile = data.files[0];
        return { id: uploadedFile.id, url: uploadedFile.url, name: uploadedFile.name, extension: uploadedFile.extension };
    } catch (err) {
        notify({ group: 'error', title: 'Upload Error', text: err.message || `Could not upload ${file.name}` });
        return null;
    }
};

const validateAndProcessFiles = async (files) => {
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const maxSizeMB = 10;
    const validFiles = Array.from(files).filter((file) => {
        const isValidType = validTypes.includes(file.type);
        const isValidSize = file.size <= maxSizeMB * 1024 * 1024;
        if (!isValidType) notify({ group: 'error', title: 'Invalid file type', text: `${file.name} - Only PNG, JPEG, DOC, and PDF files are supported.` });
        if (!isValidSize) notify({ group: 'error', title: 'File too large', text: `${file.name} - Maximum size is ${maxSizeMB}MB.` });
        return isValidType && isValidSize;
    });
    if (validFiles.length === 0) return;

    try {
        const filesToUpload = validFiles.map((file) => {
            const preview = { name: file.name, type: file.type, size: file.size, isUploading: true };
            if (file.type.includes('image')) preview.previewUrl = createObjectUrl(file);
            return { file, preview };
        });
        uploadedFiles.value = [...uploadedFiles.value, ...filesToUpload.map((f) => f.preview)];

        let successCount = 0;
        for (let i = 0; i < filesToUpload.length; i++) {
            const { file, preview } = filesToUpload[i];
            const uploadResult = await uploadToCDN(file);
            const fileIndex = uploadedFiles.value.findIndex((f) => f.name === preview.name && f.isUploading === true);
            if (uploadResult && fileIndex !== -1) {
                if (file.type.includes('image') && preview.previewUrl) revokeObjectUrl(preview.previewUrl);
                uploadedFiles.value[fileIndex] = {
                    id: uploadResult.id, name: uploadResult.name || file.name, size: file.size,
                    type: file.type, previewUrl: file.type.includes('image') ? uploadResult.url : null,
                    cdnUrl: uploadResult.url, extension: uploadResult.extension, isUploading: false,
                };
                successCount++;
            } else if (fileIndex !== -1) {
                uploadedFiles.value.splice(fileIndex, 1);
            }
        }
        if (successCount > 0) {
            const attachmentUrls = uploadedFiles.value.filter((f) => f.cdnUrl).map((f) => ({ id: f.id, url: f.cdnUrl, name: f.name, type: f.type }));
            activeRedemption.value.attachments = attachmentUrls;
            activeRedemption.value.files = attachmentUrls;
            notify({ group: 'success', title: 'Files uploaded', text: `Successfully uploaded ${successCount} file(s)` });
        }
    } catch (error) {
        notify({ group: 'error', title: 'Upload failed', text: error.message || 'There was an error uploading your files. Please try again.' });
    }
};

const onDragOver = (event) => { event.preventDefault(); isDragOver.value = true; };
const onDragLeave = (event) => { event.preventDefault(); isDragOver.value = false; };
const onDrop = (event) => { event.preventDefault(); isDragOver.value = false; validateAndProcessFiles(event.dataTransfer.files); };

async function removeFile(index) {
    const file = uploadedFiles.value[index];
    if (!file) return;
    if (file.previewUrl) revokeObjectUrl(file.previewUrl);
    uploadedFiles.value.splice(index, 1);
    try {
        const remainingIds = uploadedFiles.value.filter((f) => f && f.id).map((f) => ({ id: f.id }));
        activeRedemption.value.attachments = remainingIds;
        activeRedemption.value.files = remainingIds;
        const response = await tripsStore.updateRedemption({ keepEditModalOpen: true, silent: true, attachmentsOnly: true });
        if (response && response.status === 200) {
            const serverAttachments = response.data?.redemption?.attachments;
            if (Array.isArray(serverAttachments)) {
                activeRedemption.value.attachments = serverAttachments;
                activeRedemption.value.files = serverAttachments;
                uploadedFiles.value = serverAttachments.map((f) => ({
                    id: f.id, name: f.name || f.file_name || 'file',
                    type: f.type || f.mime_type || '',
                    previewUrl: (f.type || f.mime_type || '').includes('image')
                        ? f.url && f.file_name && !f.url.endsWith(f.file_name) ? `${f.url.replace(/\/$/, '')}/${f.file_name}` : f.url || null
                        : null,
                    cdnUrl: f.url, url: f.url, isUploading: false,
                }));
            }
            notify({ group: 'success', title: 'File deleted', text: 'The file was removed successfully.' });
        }
    } catch (error) {
        notify({ group: 'error', title: 'Failed to delete file', text: error?.message || 'There was an error removing the file. Please try again.' });
    }
}
</script>
